// src/pages/TypingTutorSettings.jsx
import React, { useState, useEffect } from 'react';
import { theme } from '../utils/theme';
import { 
  getAllTypingLessons, saveTypingLesson, deleteTypingLesson 
} from '../firebase/database';
import { generateTypingContent } from '../utils/gemini';
import { Link } from 'react-router-dom';

const C = theme.colors;

export default function TypingTutorSettings() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAI, setShowAI] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({ id: null, title: '', content: '', mode: 'english' });
  const [saving, setSaving] = useState(false);

  // AI State
  const [aiTopic, setAiTopic] = useState('');
  const [aiLang, setAiLang] = useState('english');
  const [aiLength, setAiLength] = useState('medium');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    loadLessons();
  }, []);

  const loadLessons = async () => {
    try {
      const data = await getAllTypingLessons();
      setLessons(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (lesson) => {
    setFormData(lesson);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await deleteTypingLesson(id);
      loadLessons();
    } catch (err) {
      alert("Error deleting");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveTypingLesson(formData);
      setShowForm(false);
      setFormData({ id: null, title: '', content: '', mode: 'english' });
      loadLessons();
    } catch (err) {
      alert("Error saving: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic) return alert("Enter a topic for AI");
    setAiLoading(true);
    try {
      const result = await generateTypingContent(aiTopic, aiLang, aiLength);
      setFormData({ ...formData, content: result, mode: aiLang });
      setShowAI(false);
      setShowForm(true);
    } catch (err) {
      alert("AI Generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', fontFamily: theme.fonts.sans, padding: '0 0 40px' }}>
      <style>{`
        .lesson-card { background: #fff; border: 1px solid ${C.border}; border-radius: 12px; padding: 16px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; transition: .2s; }
        .lesson-card:hover { border-color: ${C.accent}; box-shadow: ${theme.shadows.soft}; }
        .badge { font-size: 10px; font-weight: 700; padding: 2px 8px; borderRadius: 10px; text-transform: uppercase; }
        .modal { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justifyContent: center; padding: 20px; backdrop-filter: blur(2px); }
        .modal-content { background: #fff; padding: 30px; borderRadius: 20px; width: 100%; maxWidth: 600px; max-height: 90vh; overflowY: auto; }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 30 }}>
        <div>
           <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
             <Link to="/admin" style={{ color: C.muted, textDecoration: "none" }}>←</Link>
             <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Typing Tutor Settings</h1>
           </div>
           <p style={{ fontSize: 14, color: C.muted, marginLeft: 25 }}>Manage practice content and leverage Life AI</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Link to="/admin/typing-progress" style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.text, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>📈 View All Progress</Link>
          <button onClick={() => setShowAI(true)} style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.accent}`, background: C.accentSoft, color: C.accent, fontWeight: 700, cursor: 'pointer' }}>✨ AI Generator</button>
          <button onClick={() => setShowForm(true)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Add Lesson</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Loading lessons...</div>
      ) : (
        <div className="fade">
          {lessons.length === 0 ? (
            <div style={{ padding: 40, border: `2px dashed ${C.border}`, borderRadius: 20, textAlign: 'center', color: C.muted }}>
              No custom lessons yet. Click "Add Lesson" or use the AI Generator.
            </div>
          ) : (
            lessons.map(l => (
              <div key={l.id} className="lesson-card">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: C.text }}>{l.title}</span>
                    <span className="badge" style={{ background: l.mode === 'hindi' ? C.amber + '22' : C.green + '22', color: l.mode === 'hindi' ? C.amber : C.green }}>{l.mode}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 600 }}>{l.content}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleEdit(l)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 12, cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(l.id)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#ef4444', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Del</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2 style={{ marginBottom: 20 }}>{formData.id ? 'Edit Lesson' : 'Add New Lesson'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Lesson Title</label>
                <input 
                  type="text" 
                  value={formData.title} 
                  onChange={e => setFormData({ ...formData, title: e.target.value })} 
                  required
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, outline: 'none' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Practice Mode</label>
                <select 
                  value={formData.mode} 
                  onChange={e => setFormData({ ...formData, mode: e.target.value })}
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, outline: 'none' }}
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                </select>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Lesson Content</label>
                <textarea 
                  value={formData.content} 
                  onChange={e => setFormData({ ...formData, content: e.target.value })} 
                  required
                  rows="6"
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Save Lesson'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Modal */}
      {showAI && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: 400 }}>
            <h2 style={{ marginBottom: 12 }}>✨ Life AI Generator</h2>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>Generate a fresh paragraph on any topic.</p>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Topic</label>
              <input 
                type="text" 
                placeholder="e.g., Computer Basics, Science, Yoga..." 
                value={aiTopic} 
                onChange={e => setAiTopic(e.target.value)} 
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, outline: 'none' }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Language</label>
              <select 
                value={aiLang} 
                onChange={e => setAiLang(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, outline: 'none' }}
              >
                <option value="english">English</option>
                <option value="hindi">Hindi</option>
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Word Count</label>
              <select 
                value={aiLength} 
                onChange={e => setAiLength(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, outline: 'none' }}
              >
                <option value="100">100 words</option>
                <option value="500">500 words</option>
                <option value="1000">1000 words</option>
                <option value="2500">2500 words</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => setShowAI(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAIGenerate} disabled={aiLoading} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                {aiLoading ? 'Thinking...' : 'Generate →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
